// renameTable — DdlEdit builder + commit dispatcher for the
// `applyDdlEdit({ op: 'rename-table' })` op (DSE2 §3.D1).
//
// PRD: Ralph/VqeDdlEdit/PRD.md § Slice D1 — Table rename + schema-move UI.
//
// Per-engine surface:
//   - mssql:  EXEC sp_rename '<schema.tbl>', '<new>'  (rename only; schema
//             stays put — use moveTableToSchema for cross-schema moves).
//   - pgsql:  ALTER TABLE <fromTable> RENAME TO <new>
//   - mysql:  RENAME TABLE <fromTable> TO [<newSchema>.]<new>  (when
//             `mysqlToSchema` is set, this is mysql's cross-database rename
//             idiom — its natural way to do "schema move").
//   - oracle: RENAME <fromTable.bare> TO <new>  (bare-name target only;
//             oracle's RENAME requires the source table to live in the
//             current user's schema).
//   - sqlite: ALTER TABLE <fromTable> RENAME TO <new>
//
// Identifier handling: DSE2's `tableLifecycleEdit` reuses the QualifiedName
// `raw` text where possible so source quoting carries through; the target
// `toName` is bare and gets `maybeQuoteIdent` treatment per engine.

import type { SqlEngine, QualifiedName } from '../../../../sql-grammar/model';
import type { DdlEdit } from '../../../../sql-resolver/types/ddlEdit';
import { commitDdlEdit } from '../commitDdlEdit';
import { toCommitCtx, type VqeDdlEditContext } from '../editContext';

export interface RenameTableArgs {
  /** The existing table to rename. The synthesizer reuses
   *  `fromTable.raw` (when present) so source quoting + schema
   *  qualification carries through. */
  readonly fromTable: QualifiedName;
  /** New table name — bare identifier (no schema qualifier). */
  readonly toName: string;
  /** mysql-only: target schema (database) for the cross-database rename
   *  idiom (`RENAME TABLE old_db.old TO new_db.new`). Other engines reject
   *  this field at validate-time. */
  readonly mysqlToSchema?: string;
}

export function buildRenameTableEdit(args: RenameTableArgs): DdlEdit {
  return {
    op: 'rename-table',
    fromTable: args.fromTable,
    toName: args.toName,
    mysqlToSchema: args.mysqlToSchema,
  };
}

export async function commitRenameTable(
  ctx: VqeDdlEditContext,
  args: RenameTableArgs,
): Promise<void> {
  const edit = buildRenameTableEdit(args);
  await commitDdlEdit(edit, toCommitCtx(ctx));
}

/** Every supported engine accepts table rename — none are gated out. The
 *  parameter is preserved for surface-symmetry with sibling predicates
 *  (`isMoveTableToSchemaSupported`, `isAlterIndexSupported`) so consumer
 *  code can call them through the same shape. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function isRenameTableSupported(_engine: SqlEngine): boolean {
  return true;
}

/** Pre-flight validator mirroring DSE2's per-engine reject paths so the
 *  caller can surface user-friendly errors before the synthesizer would
 *  throw. Returns `null` when args are acceptable. */
export function validateRenameTableForEngine(
  engine: SqlEngine,
  args: RenameTableArgs,
): string | null {
  const trimmed = args.toName.trim();
  if (trimmed.length === 0) {
    return 'New table name is required.';
  }
  if (engine !== 'mysql' && args.mysqlToSchema !== undefined) {
    return (
      `${engine} rename-table does not accept a target schema. Use `
      + `'Move to schema' (Move table) on mssql / postgres, or a CREATE-AS-SELECT `
      + `migration on oracle / sqlite.`
    );
  }
  // Oracle's bare-name rule concerns its `RENAME` statement only: a rename
  // that rewrites the table's CREATE declaration is planned as `ALTER TABLE
  // "S"."T" RENAME TO "U"`, which Oracle accepts; the appended form still
  // refuses a qualified source in `synthesizeRenameTable`.
  return null;
}
